import { FC, useMemo, useState, useCallback } from 'react'
import styles from 'components/features/socialFeed/SocialInsight/SocialInsight.styl'
import { SocialInsightUid, SocialInsight as SocialInsightType, SocialInnerInsight } from 'interfaces/socialInsights'
import { MarkdownText } from 'components/features/socialFeed/SocialInsight/MarkdownText'
import { MarkdownMedia } from 'components/features/socialFeed/SocialInsight/MarkdownMedia'
import { CommentLikeButton } from 'components/features/pfm/SegmentButton/CommentLikeButton'
import InsightFooterContainer from 'components/features/insights/InsightFooter/InsightFooterContainer'
import { createPluralFormatter } from 'helpers/formatHelpers'
import { useStoreSelector, useShallowSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { createInsightByIdSelector } from 'selectors/insightsSelectors'
import { toggleSocialInsightLikeActionCreator } from 'actions/socialInsightsActions'
import { createPortal } from 'react-dom'
import Shownable from 'components/utils/Shownable'
import { insightShownActionCreator } from 'actions/insightsActions'
import { modifyFloatingPaneActionCreator } from 'actions/floatingPanesActions'
import { SocialProfileEnsure } from 'components/features/socialFeed/SocialProfileEnsure/SocialProfileEnsure'
import { getCreateReviewComposeId } from 'helpers/composeHelpers'
import { createSocialInsightSelector } from 'selectors/feedSelectors'
import { NewBadge } from 'components/features/pfm/NewBadge/NewBadge'
import { isMobileSelector } from 'selectors/viewportSelectors'
import { FeedLinkLoggedOut } from 'components/features/socialFeed/FeedLinkLoggedOut/FeedLinkLoggedOut'
import { useIsPublicPage } from 'helpers/pageHelpers'
import { MatchRateBadge } from 'components/features/pfm/MatchRateBadge/MatchRateBadge'
import { RewardBadge } from 'components/features/pfm/RewardBadge/RewardBadge'

const TEXT_MAX_LENGTH = 115

type SocialInsightTheme = 'regular' | 'top-opportunities'

type SocialInsightProps = {
  uid: SocialInsightUid
  index: number
  isDismissible?: boolean
  theme?: SocialInsightTheme
}

const getPluralReviews = createPluralFormatter('review', 'reviews')
const getPluralPeers = createPluralFormatter('peer', 'peers')
const getPluralPeople = createPluralFormatter('person', 'people')

export const SocialInsight: FC<SocialInsightProps> = (props) => {
  const {
    uid,
    index,
    isDismissible = true,
    theme = 'regular',
  } = props

  const { likingSocialInsightUids } = useStoreSelector((state) => state.app.opportunities)
  const isLikeLoading = Boolean(likingSocialInsightUids[uid])

  const socialInsightSelector = useMemo(createSocialInsightSelector, [])
  const postUidObj = useMemo(() => ({ postUid: uid }), [uid])
  const socialInsight = useShallowSelector((state) => {
    const entity = socialInsightSelector(state, postUidObj)
    if (!entity) {
      return null
    }

    const { placement, ...restEntity } = entity

    if (!placement) {
      return null
    }

    return {
      ...restEntity,
      placement,
    }
  })
  const insightSelector = useMemo(() => {
    if (!socialInsight || !socialInsight.placement || !socialInsight.insightId) {
      return () => null
    }
    return createInsightByIdSelector(socialInsight.placement, socialInsight.insightId)
  }, [socialInsight])

  const insight = useStoreSelector(insightSelector)

  const dispatch = useStoreDispatch()
  const onLikeClick = useCallback(() => {
    if (!socialInsight) {
      return
    }

    dispatch(toggleSocialInsightLikeActionCreator.request({
      socialInsightUid: uid,
      toLike: !socialInsight.is_liked,
    }))
  }, [dispatch, uid, socialInsight])

  if (!socialInsight || socialInsight.placement === null || !insight) {
    return null
  }

  return (
    <SocialInsightComponent
      socialInsight={ socialInsight }
      insight={ insight as SocialInnerInsight }
      isLikeLoading={ isLikeLoading }
      index={ index }
      isDismissible={ isDismissible }
      theme={ theme }
      onLikeClick={ onLikeClick }
    />
  )
}

type SocialInsightComponentProps = {
  socialInsight: SocialInsightType
  insight: SocialInnerInsight
  isLikeLoading: boolean
  index: number
  isDismissible: boolean
  theme?: SocialInsightTheme
  onLikeClick: () => void
}

const SocialInsightComponent: FC<SocialInsightComponentProps> = (props) => {
  const {
    socialInsight,
    insight,
    isLikeLoading,
    index,
    isDismissible,
    theme,
    onLikeClick,
  } = props

  const {
    uid,
    likes_count,
    comments_count,
    is_liked,
  } = socialInsight

  const {
    unique_clicks_count,
    title,
    text,
    slug,
  } = insight

  const isPublic = useIsPublicPage()

  const likesChild = useMemo(() => {
    if (!unique_clicks_count) {
      return null
    }

    const getPluralCopy = isPublic ? getPluralPeople : getPluralPeers

    return (
      <>
        <span className={ styles.peers }>
          { unique_clicks_count } { getPluralCopy(unique_clicks_count) } interested
        </span>
      </>
    )
  }, [unique_clicks_count, isPublic])

  const dispatch = useStoreDispatch()
  const handleWriteReview = useCallback(() => {
    dispatch(
      modifyFloatingPaneActionCreator('opportunity-review', {
        type: 'opportunity-review',
        payload: {
          mode: comments_count ? 'comments' : 'write-review',
          uid,
          isPublic,
        },
      })
    )
  }, [dispatch, uid, comments_count, isPublic])

  const reviewChild = useMemo(() => {
    if (comments_count > 0) {
      return (
        <span className={ styles.reviews } onClick={ handleWriteReview }>{ comments_count } { getPluralReviews(comments_count) }</span>
      )
    }

    if (isPublic) {
      return null
    }

    const composeId = getCreateReviewComposeId(uid)
    return (
      <SocialProfileEnsure composeId={ composeId } stopPropagation>
        <span className={ styles.reviews } onClick={ handleWriteReview }>Write a review</span>
      </SocialProfileEnsure>
    )
  }, [comments_count, handleWriteReview, uid, isPublic])

  const socialChild = useMemo(() => {
    if (!likesChild && !reviewChild) {
      return null
    }

    const delimiterChild = likesChild && reviewChild
      ? (
        <>
          &nbsp;|&nbsp;
        </>
      )
      : null
    return (
      <div className={ styles.social }>
        { likesChild }{ delimiterChild }{ reviewChild }
      </div>
    )
  }, [likesChild, reviewChild])

  const [isCutted, setIsCutted] = useState(true)

  const handleReadMore = useCallback(() => {
    setIsCutted(false)
  }, [])

  const textChild = useMemo(() => (
    <MarkdownText
      text={ text }
      maxLength={ isCutted ? TEXT_MAX_LENGTH : null }
      onReadMore={ handleReadMore }
    />
  ), [text, isCutted, handleReadMore])

  const mediaChild = useMemo(() => (
    <MarkdownMedia text={ text } />
  ), [text])

  const [annotationsNode, setAnnotationsNode] = useState<HTMLDivElement | null>(null)
  const handleAnnotationsNode = useCallback<(node: HTMLDivElement | null) => void>((node) => {
    if (annotationsNode === node) {
      return
    }
    setAnnotationsNode(node)
  }, [annotationsNode])

  const AnnotationsContainer = useMemo<FC>(() => {
    return ({ children }) => {
      if (!annotationsNode) {
        return null
      }
      return createPortal(children, annotationsNode)
    }
  }, [annotationsNode])

  const handleShown = useCallback(() => {
    dispatch(insightShownActionCreator(insight))
  }, [dispatch, insight])

  const actionsChild = useMemo(() => (
    <div className={ styles.actions }>
      <div className={ styles.annotations } ref={ handleAnnotationsNode } />
      <div className={ styles.buttonsLeft }>
        { isPublic && (
          <div className={ styles.publicLike }>
            <FeedLinkLoggedOut type='offers'>
              <CommentLikeButton counter={ likes_count } />
            </FeedLinkLoggedOut>
          </div>
        )}
        { !isPublic && (
          <MatchRateBadge
            insight={ insight }
            index={ index }
            AnnotationsContainer={ AnnotationsContainer }
          />
        )}
        <RewardBadge
          insight={ insight }
          AnnotationsContainer={ AnnotationsContainer }
        />
        <div className={ styles.otherBadges }>
          <NewBadge insight={ insight } />
        </div>
      </div>

      { !isPublic && (
        <div className={ styles.buttonsRight }>
          <CommentLikeButton
            disabled={ isLikeLoading }
            pressed={ is_liked }
            counter={ likes_count }
            onClick={ onLikeClick }
          />
        </div>
      )}
    </div>
  ), [
    AnnotationsContainer, handleAnnotationsNode, index,
    insight, isLikeLoading, is_liked, likes_count, onLikeClick, isPublic,
  ])

  const isMobile = useStoreSelector(isMobileSelector)

  const ctaChild = useMemo(() => {
    let actualInsight = insight

    if (!isDismissible && insight.cta) {
      const { dismiss, ...cta } = insight.cta
      actualInsight = {
        ...insight,
        cta,
      }
    }

    return (
      <div className={ styles.cta }>
        <InsightFooterContainer
          insight={ actualInsight }
          fullWidth={ theme === 'top-opportunities' || !isMobile }
          narrowDismiss
          buttonSize='tiny'
        />
      </div>
    )
  }, [insight, isDismissible, theme, isMobile])

  return (
    <Shownable
      onShown={ handleShown }
    >
      <div data-slug={ slug }>
        <div className={ styles.socialInsight }>
          <div className={ styles.media }>
            <div className={ styles.mediaLayout }>
              { mediaChild }
            </div>
          </div>
          <div className={ styles.gap } />
          <div className={ styles.title }>{ title }</div>
          { socialChild }
          <div className={ styles.text }>
            { textChild }
          </div>
          { actionsChild }
          { ctaChild }
        </div>
      </div>
    </Shownable>
  )
}
